/**
 * API Thunk Creator
 * 
 * This file provides utilities for creating standardized Redux async thunks
 * for API operations. It reduces boilerplate and ensures consistent parameter
 * handling, error handling, and response processing.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  RESOURCE_TYPES,
  RESOURCE_ID_PARAMS,
  HTTP_METHODS,
  OPERATION_TYPES,
  METHOD_TO_OPERATION,
  DEFAULT_ERROR_MESSAGES,
  capitalize,
  generateTypePrefix
} from '../schemas/parameterSchema';
import {
  validateResourceId,
  extractResourceId,
  extractData,
  sanitizeParams
} from '../utils/parameterUtils';

/**
 * Create a standardized async thunk for API operations
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.typePrefix - The Redux action type prefix
 * @param {string} options.resourceType - The type of resource (e.g., 'character')
 * @param {string} options.method - The HTTP method to use (get, post, put, delete)
 * @param {string} options.routeKey - The API route key from apiRoutes
 * @param {string} options.paramName - The parameter name for the resource ID
 * @param {string} options.errorMessage - The error message for validation failures
 * @param {Function} options.transformResponse - Optional function to transform the response data
 * @param {Object} options.extraParams - Additional parameters to include in the request
 * @param {Function} options.onSuccess - Optional callback to run on successful response
 * @param {Function} options.onError - Optional callback to run on error
 * @param {string} options.hintText - Optional text to log during execution
 * @param {boolean} options.requiresId - Whether this operation requires a resource ID (default: true)
 * @param {Object} options.paramMapping - Optional mapping of parameter names to route parameter names
 * @returns {AsyncThunk} A configured createAsyncThunk function
 */
export const createApiThunk = ({
  typePrefix,
  resourceType,
  method = HTTP_METHODS.GET,
  routeKey,
  paramName = null, // If null, uses the default for the resource type
  errorMessage = null, // If null, generates a default message
  transformResponse = (data) => data,
  extraParams = {},
  onSuccess = null,
  onError = null,
  hintText = null,
  requiresId = true, // New parameter to control ID validation
  paramMapping = null // Optional mapping of parameter names to route parameter names
}) => {
  // Get the parameter name if not provided
  const idParam = paramName || RESOURCE_ID_PARAMS[resourceType];

  // Generate default error message if not provided
  const defaultErrorMessage = DEFAULT_ERROR_MESSAGES.MISSING_ID(resourceType);
  const validationErrorMessage = errorMessage || defaultErrorMessage;

  return createAsyncThunk(
    typePrefix,
    async (param, { rejectWithValue, dispatch, getState }) => {
      try {
        // Import the API client and routes
        // We do this inside the thunk to avoid circular dependencies
        const { apiClient } = require('../../apiTools/apiClient');
        const { apiRoutes } = require('../../apiTools/constants/apiRoutes');

        // Get the route from the routeKey
        const route = getRouteFromKey(apiRoutes, routeKey);
        if (!route) {
          console.error(`Invalid route key: ${routeKey}`);
          return rejectWithValue(`Invalid route key: ${routeKey}`);
        }

        // For methods that require an ID parameter
        let resourceId = null;
        if ((method !== HTTP_METHODS.POST || idParam) && requiresId) {
          // Extract and validate the resource ID
          const validation = validateResourceId(param, resourceType);

          if (!validation.isValid) {
            console.error(`${typePrefix}: ${validation.error}`, param);
            return rejectWithValue(validationErrorMessage);
          }

          resourceId = validation.id;
          console.log(`${typePrefix}: Operation on ${resourceType} with ID: ${resourceId}`);
        }

        // Prepare parameters
        const params = resourceId ? {
          [idParam]: resourceId,
          ...extraParams
        } : { ...extraParams };

        // Extract data for POST, PUT, PATCH operations
        let data = {};
        if (method !== HTTP_METHODS.GET && method !== HTTP_METHODS.DELETE) {
          data = extractData(param, resourceType);
        }

        let response;

        // Apply parameter mapping to handle aliases
        const sanitizedParams = sanitizeParams(params, paramMapping);

        // Use the appropriate API client method
        switch (method) {
          case HTTP_METHODS.GET:
            response = await apiClient.get(route, sanitizedParams);
            break;
          case HTTP_METHODS.POST:
            response = await apiClient.post(route, data, sanitizedParams);
            break;
          case HTTP_METHODS.PUT:
            response = await apiClient.put(route, data, sanitizedParams);
            break;
          case HTTP_METHODS.PATCH:
            response = await apiClient.patch(route, data, sanitizedParams);
            break;
          case HTTP_METHODS.DELETE:
            response = await apiClient.delete(route, sanitizedParams);
            break;
          default:
            throw new Error(`Unsupported HTTP method: ${method}`);
        }

        if (hintText) console.log(hintText);
        // Transform the response data
        const transformedData = transformResponse(response.data, { param, resourceId, getState });

        // Call onSuccess callback if provided
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(transformedData, { dispatch, getState });
        }

        return transformedData;
      } catch (error) {
        console.error(`Error in ${typePrefix}:`, error);

        // Call onError callback if provided
        if (onError && typeof onError === 'function') {
          onError(error, { dispatch, getState });
        }

        // Extract error message from response data if available
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            return rejectWithValue(error.response.data);
          } else if (error.response.data.error && typeof error.response.data.error === 'string') {
            return rejectWithValue(error.response.data.error);
          } else if (error.response.data.message && typeof error.response.data.message === 'string') {
            return rejectWithValue(error.response.data.message);
          }
        }

        // Fallback to error message or default
        return rejectWithValue(error.message || 'Unknown error');
      }
    }
  );
};

/**
 * Helper function to get a route from a dot-notation key
 * 
 * @param {Object} routes - The routes object
 * @param {string} key - The dot-notation key (e.g., 'CHARACTERS.DETAIL')
 * @returns {string|null} The route, or null if not found
 */
const getRouteFromKey = (routes, key) => {
  if (!key || typeof key !== 'string') {
    return null;
  }

  const parts = key.split('.');
  let current = routes;

  for (const part of parts) {
    if (!current || typeof current !== 'object' || !(part in current)) {
      return null;
    }
    current = current[part];
  }

  return typeof current === 'string' ? current : null;
};

/**
 * Create a GET thunk
 * 
 * @param {Object} options - Configuration options
 * @returns {AsyncThunk} A configured createAsyncThunk function for GET requests
 */
export const createGetThunk = (options) => {
  return createApiThunk({
    ...options,
    method: HTTP_METHODS.GET
  });
};

/**
 * Create a GET thunk for collection endpoints
 * 
 * @param {Object} options - Configuration options
 * @returns {AsyncThunk} A configured createAsyncThunk function for GET requests that don't require an ID
 */
export const createGetCollectionThunk = (options) => {
  return createApiThunk({
    ...options,
    method: HTTP_METHODS.GET,
    requiresId: false
  });
};

/**
 * Create a POST thunk
 * 
 * @param {Object} options - Configuration options
 * @returns {AsyncThunk} A configured createAsyncThunk function for POST requests
 */
export const createPostThunk = (options) => {
  return createApiThunk({
    ...options,
    method: HTTP_METHODS.POST
  });
};

/**
 * Create a PUT thunk
 * 
 * @param {Object} options - Configuration options
 * @returns {AsyncThunk} A configured createAsyncThunk function for PUT requests
 */
export const createPutThunk = (options) => {
  return createApiThunk({
    ...options,
    method: HTTP_METHODS.PUT
  });
};

/**
 * Create a DELETE thunk
 * 
 * @param {Object} options - Configuration options
 * @returns {AsyncThunk} A configured createAsyncThunk function for DELETE requests
 */
export const createDeleteThunk = (options) => {
  return createApiThunk({
    ...options,
    method: HTTP_METHODS.DELETE
  });
};

/**
 * Create a PATCH thunk
 * 
 * @param {Object} options - Configuration options
 * @returns {AsyncThunk} A configured createAsyncThunk function for PATCH requests
 */
export const createPatchThunk = (options) => {
  return createApiThunk({
    ...options,
    method: HTTP_METHODS.PATCH
  });
};

/**
 * Create a fetch resource thunk
 * 
 * @param {string} resourceType - The type of resource
 * @param {string} routeKey - The API route key
 * @param {Object} options - Additional options
 * @returns {AsyncThunk} A configured createAsyncThunk function for fetching a resource
 */
export const createFetchResourceThunk = (resourceType, routeKey, options = {}) => {
  return createGetThunk({
    typePrefix: generateTypePrefix(resourceType, OPERATION_TYPES.FETCH),
    resourceType,
    routeKey,
    ...options
  });
};

/**
 * Create a fetch all resources thunk
 * 
 * @param {string} resourceType - The type of resource
 * @param {string} routeKey - The API route key
 * @param {Object} options - Additional options
 * @returns {AsyncThunk} A configured createAsyncThunk function for fetching all resources
 */
export const createFetchAllResourcesThunk = (resourceType, routeKey, options = {}) => {
  return createGetThunk({
    typePrefix: generateTypePrefix(resourceType, OPERATION_TYPES.FETCH_ALL),
    resourceType,
    routeKey,
    ...options
  });
};

/**
 * Create a fetch collection thunk
 * 
 * @param {string} resourceType - The type of resource
 * @param {string} routeKey - The API route key
 * @param {Object} options - Additional options
 * @returns {AsyncThunk} A configured createAsyncThunk function for fetching a collection
 */
export const createFetchCollectionThunk = (resourceType, routeKey, options = {}) => {
  return createGetCollectionThunk({
    typePrefix: `${resourceType}/fetchAll${capitalize(resourceType)}s`,
    resourceType,
    routeKey,
    ...options
  });
};

/**
 * Create a create resource thunk
 * 
 * @param {string} resourceType - The type of resource
 * @param {string} routeKey - The API route key
 * @param {Object} options - Additional options
 * @returns {AsyncThunk} A configured createAsyncThunk function for creating a resource
 */
export const createCreateResourceThunk = (resourceType, routeKey, options = {}) => {
  return createPostThunk({
    typePrefix: generateTypePrefix(resourceType, OPERATION_TYPES.CREATE),
    resourceType,
    routeKey,
    ...options
  });
};

/**
 * Create an update resource thunk
 * 
 * @param {string} resourceType - The type of resource
 * @param {string} routeKey - The API route key
 * @param {Object} options - Additional options
 * @returns {AsyncThunk} A configured createAsyncThunk function for updating a resource
 */
export const createUpdateResourceThunk = (resourceType, routeKey, options = {}) => {
  return createPutThunk({
    typePrefix: generateTypePrefix(resourceType, OPERATION_TYPES.UPDATE),
    resourceType,
    routeKey,
    ...options
  });
};

/**
 * Create a delete resource thunk
 * 
 * @param {string} resourceType - The type of resource
 * @param {string} routeKey - The API route key
 * @param {Object} options - Additional options
 * @returns {AsyncThunk} A configured createAsyncThunk function for deleting a resource
 */
export const createDeleteResourceThunk = (resourceType, routeKey, options = {}) => {
  return createDeleteThunk({
    typePrefix: generateTypePrefix(resourceType, OPERATION_TYPES.DELETE),
    resourceType,
    routeKey,
    ...options
  });
};

/**
 * Create a complete set of CRUD thunks for a resource
 * 
 * @param {string} resourceType - The type of resource
 * @param {Object} routeKeys - The API route keys for each operation
 * @param {Object} options - Additional options for each operation
 * @returns {Object} An object containing all the CRUD thunks
 */
export const createResourceThunks = (
  resourceType,
  routeKeys = {},
  options = {}
) => {
  const defaultRouteKeys = {
    fetch: `${resourceType.toUpperCase()}S.DETAIL`,
    fetchAll: `${resourceType.toUpperCase()}S.BASE`,
    create: `${resourceType.toUpperCase()}S.BASE`,
    update: `${resourceType.toUpperCase()}S.DETAIL`,
    delete: `${resourceType.toUpperCase()}S.DETAIL`
  };

  const mergedRouteKeys = { ...defaultRouteKeys, ...routeKeys };

  return {
    [`fetch${capitalize(resourceType)}`]: createFetchResourceThunk(
      resourceType,
      mergedRouteKeys.fetch,
      options.fetch || {}
    ),
    [`fetchAll${capitalize(resourceType)}s`]: createFetchCollectionThunk(
      resourceType,
      mergedRouteKeys.fetchAll,
      options.fetchAll || {}
    ),
    [`create${capitalize(resourceType)}`]: createCreateResourceThunk(
      resourceType,
      mergedRouteKeys.create,
      options.create || {}
    ),
    [`update${capitalize(resourceType)}`]: createUpdateResourceThunk(
      resourceType,
      mergedRouteKeys.update,
      options.update || {}
    ),
    [`delete${capitalize(resourceType)}`]: createDeleteResourceThunk(
      resourceType,
      mergedRouteKeys.delete,
      options.delete || {}
    )
  };
};

/**
 * Create a thunk for fetching a signed URL using the raw path approach
 * This is a specialized wrapper around createAsyncThunk for the specific
 * use case of getting signed URLs with a raw URL and userId.
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.typePrefix - The Redux action type prefix
 * @param {string} options.routeKey - The API route key from apiRoutes
 * @param {Function} options.transformResponse - Optional function to transform the response data
 * @param {Function} options.onSuccess - Optional callback to run on successful response
 * @param {Function} options.onError - Optional callback to run on error
 * @param {string} options.hintText - Optional text to log during execution
 * @returns {AsyncThunk} A configured createAsyncThunk function
 */
export const createSignedUrlThunk = (options = {}) => {
  const {
    typePrefix = 'signedUrl/getSignedUrl',
    routeKey = 'UTILS.GET_SIGNED_URL_BY_RAW_PATH',
    transformResponse = (data, { param }) => ({
      originalUrl: param.url,
      signedUrl: data.signedUrl,
      userId: param.userId
    }),
    onSuccess = null,
    onError = null,
    hintText = null
  } = options;

  return createAsyncThunk(
    typePrefix,
    async ({ url, userId }, { rejectWithValue, dispatch, getState }) => {
      try {
        // Import the API client and routes
        // We do this inside the thunk to avoid circular dependencies
        const { apiClient } = require('../../apiTools/apiClient');
        const { apiRoutes } = require('../../apiTools/constants/apiRoutes');

        // Get the route from the routeKey
        const route = getRouteFromKey(apiRoutes, routeKey);
        if (!route) {
          console.error(`Invalid route key: ${routeKey}`);
          return rejectWithValue(`Invalid route key: ${routeKey}`);
        }

        if (hintText) console.log(hintText);

        // Make the request with url and userId in the body
        const response = await apiClient.post(route, { url, userId });

        // Transform the response
        const transformedData = transformResponse(response.data, {
          param: { url, userId },
          getState
        });

        // Call onSuccess callback if provided
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(transformedData, { dispatch, getState });
        }

        return transformedData;
      } catch (error) {
        console.error(`Error in ${typePrefix}:`, error);

        // Call onError callback if provided
        if (onError && typeof onError === 'function') {
          onError(error, { dispatch, getState });
        }

        // Extract error message from response data if available
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            return rejectWithValue(error.response.data);
          } else if (error.response.data.error && typeof error.response.data.error === 'string') {
            return rejectWithValue(error.response.data.error);
          } else if (error.response.data.message && typeof error.response.data.message === 'string') {
            return rejectWithValue(error.response.data.message);
          }
        }

        // Fallback to error message or default
        return rejectWithValue(error.message || 'Unknown error');
      }
    }
  );
};